import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { Theme, useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import produce from "immer";
import { UseMutationResult, useQueryClient } from "react-query";
import {
  DropTargetMonitor,
  DragSource,
  DropTarget,
  DropTargetConnector,
  DragSourceConnector,
  ConnectDropTarget,
  DragSourceMonitor,
  ConnectDragSource,
  ConnectDragPreview,
} from "react-dnd";

import {
  Series,
  Module,
  Requirement,
  Response,
} from "services/KnowfullyAdminClient";
import { DragItem, DraggableInstance, shouldMove } from "utils/dndUtils";
import useBoolean from "hooks/useBoolean";
import { Mode } from "types/common-types";

import ModuleUpdateForm from "./ModuleUpdateForm";
import OrderableModuleRequirements from "./OrderableRequirements";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import { useIntl } from "react-intl";

type DraggableModuleProps = {
  index: number;
  module: Module;
  series: Series;
  mode: Mode;
  onMoveModule: (fromIndex: number, toIndex: number) => void;
  onDropModule: () => void;
  deleteModule: (moduleId: string) => void;
  
  reorderRequirementsMutation: UseMutationResult<
    Response,
    unknown,
    { moduleId: string; orderedRequirements: Requirement[] },
    unknown
  >;

  // Supplied by react-dnd HOCs
  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
};

const useStyles = makeStyles((theme: Theme) => ({
  editActions: {
    width: 100,
  },
  quantity: {
    width: 80,
  },
  dragHandle: {
    width: 30,
    display: "flex",
    alignItems: "center",
    cursor: "move",
  },
}));

const DraggableModule = forwardRef<HTMLDivElement, DraggableModuleProps>(
  (
    {
      module,
      series,
      mode,
      deleteModule,
      reorderRequirementsMutation,

      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
    },
    ref
  ) => {
    const styles = useStyles(useTheme());
    const { formatMessage: t } = useIntl();
    const queryClient = useQueryClient();
    const [updateOpen, updateOpenActions] = useBoolean(false);
    const [isExpanded, isExpandedActions] = useBoolean(false);
    const [deleteOpen, deleteOpenActions] = useBoolean(false);
    const [deleting, setDeleting] = useState<Module | null>(null);
    const isUpdate = mode === Mode.Update;
    const elementRef = useRef(null);
    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    connectDragPreview(connectDropTarget(elementRef));

    useImperativeHandle<any, DraggableInstance>(ref, () => ({
      getNode: () => elementRef.current,
    }));

    const opacity = isDragging ? 0 : 1;

    const onRequirementChange = () => {
      queryClient.invalidateQueries(["seriesById", series.id]);
      updateOpenActions.setFalse();
    };

    const onModuleUpdated = () => {
      queryClient.invalidateQueries(["seriesById", series.id]);
      updateOpenActions.setFalse();
    };

    const handleDelete = useCallback(async () => {
      const id = deleting?.id;
      setDeleting(null);
      if (id) {
        try {
          deleteModule(module.id);
        } catch (err: any) {
          console.log(err);
          return { [FORM_ERROR]: translateError(err, t) };
        }
      }
    }, [deleteModule, deleting?.id, module.id, t]);

    return (
      <Box mb={2}>
        <div
          ref={elementRef}
          style={{ opacity, transform: "translate3d(0, 0, 0)" }}
        >
          <Accordion
            square
            expanded={isExpanded}
            onChange={(_, expanded) => isExpandedActions.setValue(expanded)}
          >
            <AccordionSummary
              style={{ margin: 0 }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Box display="flex" alignItems="center" flex={1}>
                {isUpdate && connectDragSource(
                  <div>
                    <Box sx={styles.dragHandle}>
                      <DragHandleIcon />
                    </Box>
                  </div>
                )}
                <Box flexGrow={1}>
                  <Typography>{module.name}</Typography>
                </Box>
                <Box sx={styles.quantity}>
                  <Typography>{module.quantity ?? 0}</Typography>
                </Box>
                <Box sx={styles.editActions}>
                  <IconButton
                    disabled={!isUpdate}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateOpenActions.setTrue();
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    disabled={!isUpdate}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleting(module);
                      deleteOpenActions.setTrue();
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {isExpanded && (
                <OrderableModuleRequirements
                  series={series}
                  mode={mode}
                  module={module}
                  onRequirementDeleted={onRequirementChange}
                  onRequirementUpdated={onRequirementChange}
                  reorderRequirementsMutation={reorderRequirementsMutation}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </div>

        <DeleteEntityDialog
          dialogTitleKey={"moduleDeleteTitle"}
          contentKey={"moduleDeleteQuestion"}
          onCancel={deleteOpenActions.setFalse}
          handleDelete={handleDelete}
          open={deleteOpen}
        />

        <Dialog open={updateOpen} maxWidth="sm" fullWidth disableEnforceFocus>
          <ModuleUpdateForm
            onModuleUpdated={onModuleUpdated}
            onCancel={updateOpenActions.setFalse}
            series={series}
            module={module}
          />
        </Dialog>
      </Box>
    );
  }
);

const EnabledDraggableModule = DropTarget(
  "SERIES_MODULE",
  {
    drop({ onDropModule }: DraggableModuleProps) {
      onDropModule();
    },
    hover(
      { index, onMoveModule }: DraggableModuleProps,
      monitor: DropTargetMonitor,
      component: DraggableInstance
    ) {
      if (!component) {
        return null;
      }

      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const item = monitor.getItem<DragItem>();
      const dragIndex = item.index;
      const hoverIndex = index;

      if (shouldMove(hoverIndex, node, dragIndex, monitor)) {
        onMoveModule(item.index, index);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    "SERIES_MODULE",
    {
      beginDrag: ({ module, index }: DraggableModuleProps) => ({
        id: module.id,
        index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    })
  )(DraggableModule)
);

type OrderableModulesProps = {
  series: Series;
  mode: Mode;
  modules: Module[];
  deleteModuleMutation: UseMutationResult<
    void,
    unknown,
    { moduleId: string },
    unknown
  >;
  reorderModulesMutation: UseMutationResult<
    Response,
    unknown,
    Module[],
    unknown
  >;
  reorderRequirementsMutation: UseMutationResult<
    Response,
    unknown,
    { moduleId: string; orderedRequirements: Requirement[] },
    unknown
  >;
};

const OrderableModules = ({
  series,
  mode,
  modules,
  deleteModuleMutation,
  reorderModulesMutation,
  reorderRequirementsMutation,  
}: OrderableModulesProps) => {
  const [orderedModules, setOrderedModules] = useState(modules);
  const deleteModule = useCallback(
    (moduleId: string) => deleteModuleMutation.mutate({ moduleId }),
    [deleteModuleMutation]
  );

  const onMoveModule = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragModule = orderedModules![dragIndex];
      const updatedModuleOrder = produce(orderedModules!, (draft) => {
        draft.splice(dragIndex, 1);
        draft.splice(hoverIndex, 0, dragModule);
      });
      setOrderedModules(updatedModuleOrder);
    },
    [orderedModules]
  );

  const onDropModule = useCallback(
    () => reorderModulesMutation.mutate(orderedModules),
    [orderedModules, reorderModulesMutation]
  );

  useEffect(() => {
    setOrderedModules(modules);
  }, [modules]);

  return (
    <div>
      {orderedModules.map((module, i) => {
        return (
          <EnabledDraggableModule
            key={module.id}
            module={module}
            series={series}
            mode={mode}
            index={i}
            deleteModule={deleteModule}
            onMoveModule={onMoveModule}
            onDropModule={onDropModule}
            reorderRequirementsMutation={reorderRequirementsMutation}
          />
        );
      })}
    </div>
  );
};

export default OrderableModules;
